import React, { useState, useEffect } from "react"
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, IconButton } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import FileDropzone, { FilesItem } from "src/sites/file/droparea/FileDropzone"
import { UploadedJobFile } from "src/sites/file/droparea/expiringFiles"
import CustomAutocomplete from "src/components/CustomAutocomplete"
import { useGlobalLoader, useLogger } from "src/providers"
import { useSnackbar } from "src/providers/SnackbarProvider"
import { useResponsive } from "src/utils/useResponsive"
import { useLocalStorage } from "src/utils/useLocalStorage"
import { useDataSourceFilter } from "src/sites/data-sources/useDataSourceFilter"
import { DataSourceOption } from "./DataSourcesFilter"
import { ingestFilesToKnowledgeBases } from "src/utils/kmIngest"
import { getSupportedFileTypes, SupportedFileType } from "src/km/km"

interface UploadSourcesModalProps {
  open: boolean
  onClose: () => void
  onUpload: () => void
}

const UploadSourcesModal: React.FC<UploadSourcesModalProps> = ({ open, onClose, onUpload }) => {
  const { showLoader, hideLoader } = useGlobalLoader()
  const { snackbarShowMessage } = useSnackbar()
  const { isMobile } = useResponsive()
  const { logger } = useLogger()
  const { dataSourceOptions } = useDataSourceFilter()
  const [files, setFiles] = useState<FilesItem[]>([])
  const [supportedFileTypes, setSupportedFileTypes] = useState<SupportedFileType[]>([])
  const [knowledgeBaseError, setKnowledgeBaseError] = useState<string | null>(null)
  const [selectedDataSource, setSelectedDataSource] = useLocalStorage<DataSourceOption[]>(
    "selectedDataSource",
    dataSourceOptions || [],
    (value: DataSourceOption[]) => value.every((v) => dataSourceOptions?.some((o) => o.value === v.value)),
    [dataSourceOptions]
  )

  useEffect(() => {
    getSupportedFileTypes()
      .then((types) => {
        setSupportedFileTypes(types)
        setKnowledgeBaseError(null)
      })
      .catch((error) => {
        logger.error("Failed to fetch supported file types:", error)
        setKnowledgeBaseError("We're experiencing issues with our Knowledge Base. Please try again later.")
      })
  }, [])
  const uploadDataSourceFile = (file: File) => {
    return new Promise<UploadedJobFile>((resolve, reject) => {
      resolve({ name: file.name, id: file.name, size: file.size })
    })
  }

  const handleSubmit = async () => {
    showLoader()

    const uploadFiles = files
      .map((file) => file.toUploadFile?.file)
      .filter((file): file is File => file !== undefined)

    const destinations = selectedDataSource.map((ds) => ({
      id: ds.value,
      name: ds.label
    }))

    try {
      const results = await ingestFilesToKnowledgeBases(uploadFiles, destinations)

      const errors = results.filter((result) => result.status === "error")

      if (errors.length > 0) {
        const errorMessages = errors.map((error) => error.error).join("\n")
        snackbarShowMessage(errorMessages, "error")
      }

      const hasAnySuccess = results.some((result) => result.status === "success")

      if (hasAnySuccess) {
        onUpload()
      }
      onClose()
    } finally {
      hideLoader()
      setFiles([])
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      fullScreen={isMobile}
      maxWidth={isMobile ? false : "md"}
      PaperProps={{
        sx: {
          px: { xs: 2, md: 5 },
          py: { xs: 1, md: 3 },
          ...(isMobile && {
            borderRadius: 0,
            margin: 0,
            height: "100dvh",
            maxHeight: "100dvh"
          })
        }
      }}
    >
      <DialogTitle
        variant={isMobile ? "h4" : "h3"}
        sx={{
          px: 0,
          ...(isMobile && {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 2
          })
        }}
      >
        Upload sources
        {isMobile && (
          <IconButton
            onClick={onClose}
            sx={{
              color: "text.primary",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)"
              }
            }}
            aria-label="close"
          >
            <CloseIcon fontSize="large" />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "auto",
          minHeight: "400px",
          gap: { xs: 2 }
        }}
      >
        <Box sx={isMobile ? { display: "flex", flex: 1 } : { height: "310px" }}>
          {knowledgeBaseError ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", textAlign: "center", px: 2 }}>
              <Typography color="error">{knowledgeBaseError}</Typography>
            </Box>
          ) : supportedFileTypes.length > 0 ? (
            <FileDropzone
              id={"data-sources-dropzone"}
              files={files}
              setFiles={setFiles}
              fileFormats={supportedFileTypes.map(type => type.extension)}
              maxSizeDescription={`30.00 MB or 500 pages.\nVideo limit: 200.00 MB.`}
              maxSize={{
                default: 30 * 1024 * 1024,
                byExtension: {
                  ".mp4": 200 * 1024 * 1024
                },
                byMimeType: {
                  "video/mp4": 200 * 1024 * 1024
                }
              }}
              fileTypes={supportedFileTypes.flatMap(type => [type.mimeType, type.extension])}
              placing="Data sources upload"
              uploadFiles={uploadDataSourceFile}
            />
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
              <Typography>Loading supported file types...</Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ flexGrow: 0, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="subtitle1">Add to following:</Typography>
          <CustomAutocomplete
            value={selectedDataSource}
            options={dataSourceOptions}
            getOptionLabel={(option) => option.label}
            onChange={(e, val) => val && setSelectedDataSource(val as DataSourceOption[])}
            label="Knowledge Base"
            isOptionEqualToValue={(o1, o2) => o1.value === o2.value}
            size="small"
            multiple
            disabled={isMobile}
          />
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          p: 0,
          pb: 3,
          mt: { xs: 2 },
          gap: { xs: 1 }
        }}
      >
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            !files.length ||
            selectedDataSource.length === 0 ||
            files.some((file) => file.uploadedFile?.typedValidationError)
          }
          fullWidth={isMobile}
        >
          Upload
        </Button>
        <Button variant="outlined" onClick={onClose} fullWidth={isMobile}>
          Dismiss
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default UploadSourcesModal
